import { FC, useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, Stack, styled, Tooltip } from "@mui/material";
import { IconChevronDown } from "@/assets/icons";
import { background, divider, text, black } from "@/theme/colors";
import { Select } from "@/components/elements/Select";
import { TOption } from "@/types/common";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { truncateElementToWidth } from "@/utils/string";

type TRow = {
  label: string;
  months: (number | string | null)[];
  total: number | string | null;
  previousYearTotal?: number | string | null;
  isHeader?: boolean;
  indent?: number;
};

type TAnnualStatTableProps = {
  title: string;
  rows: TRow[];
  showPreviousYearColumn?: boolean;
  isLoading?: boolean;
  statusOptions?: TOption[];
  selectedStatus?: string;
  onStatusChange?: (value: string) => void;
  targetMonth?: number;
  firstColumnLabel?: string;
  firstColumnWidth?: number;
  showFooter?: boolean;
  rightContent?: React.ReactNode;
};

// Fiscal year starts the month after the closing month (targetMonth)
const getMonthLabels = (targetMonth: number): string[] => {
  const startMonth = (targetMonth % 12) + 1;
  return Array.from({ length: 12 }, (_, i) => {
    const month = ((startMonth - 1 + i) % 12) + 1;
    return `${month}月`;
  });
};

const formatCell = (value: number | string | null): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return value.toLocaleString();
};

const sumColumn = (rows: TRow[], colIndex: number): number | null => {
  let sum = 0;
  let hasValue = false;
  for (const row of rows) {
    const v = colIndex === -1 ? row.total : row.months[colIndex];
    if (typeof v === "number") {
      sum += v;
      hasValue = true;
    }
  }
  return hasValue ? sum : null;
};

// Truncates the label to the fixed-width sticky cell with a baseline "..." (see truncateElementToWidth).
// Text is set imperatively on the element; tooltip shows full text on hover only when truncated.
const EllipsisLabel: FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const compute = () => setTruncated(truncateElementToWidth(el, text));
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <Tooltip title={truncated ? text : ""} placement="top-start">
      <ClipText ref={ref} />
    </Tooltip>
  );
};

export const AnnualStatTable: FC<TAnnualStatTableProps> = ({
  title,
  rows,
  showPreviousYearColumn = false,
  isLoading,
  statusOptions,
  selectedStatus,
  onStatusChange,
  targetMonth = 3,
  firstColumnLabel = "対象",
  showFooter = true,
  rightContent,
}) => {
  const monthLabels = getMonthLabels(targetMonth);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="head20Bold" sx={{ fontSize: "16px" }}>
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          {rightContent}
          {statusOptions && selectedStatus && onStatusChange && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="cap12">集計範囲</Typography>
              <SelectWithChevron>
                <Select options={statusOptions} value={selectedStatus} onChange={onStatusChange} size="md" clearable={false} nonControl />
                <ChevronIcon>
                  <IconChevronDown color={black[80]} width={16} height={16} />
                </ChevronIcon>
              </SelectWithChevron>
            </Stack>
          )}
        </Stack>
      </Stack>
      {isLoading ? (
        <LoadingArea />
      ) : (
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <HeaderCell $sticky>{firstColumnLabel}</HeaderCell>
                {monthLabels.map((label) => (
                  <HeaderCell key={label}>{label}</HeaderCell>
                ))}
                <HeaderCell $isTotal>合計</HeaderCell>
                {showPreviousYearColumn && <HeaderCell>前年度合計</HeaderCell>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <BodyCell $sticky $isHeader={row.isHeader} $indent={row.indent}>
                    <EllipsisLabel text={row.label} />
                  </BodyCell>
                  {row.months.map((value, monthIndex) => (
                    <BodyCell key={monthIndex} $isHeader={row.isHeader}>
                      {formatCell(value)}
                    </BodyCell>
                  ))}
                  <BodyCell $isHeader={row.isHeader} $isTotal>
                    {formatCell(row.total)}
                  </BodyCell>
                  {showPreviousYearColumn && <BodyCell $isHeader={row.isHeader}>{formatCell(row.previousYearTotal ?? null)}</BodyCell>}
                </tr>
              ))}
            </tbody>
            {showFooter && rows.length > 0 && (
              <tfoot>
                <tr>
                  <FooterCell $sticky>合計</FooterCell>
                  {monthLabels.map((_, i) => (
                    <FooterCell key={i}>{formatCell(sumColumn(rows, i))}</FooterCell>
                  ))}
                  <FooterCell $isTotal>{formatCell(sumColumn(rows, -1))}</FooterCell>
                  {showPreviousYearColumn && <FooterCell>{formatCell(null)}</FooterCell>}
                </tr>
              </tfoot>
            )}
          </StyledTable>
        </TableWrapper>
      )}
    </Box>
  );
};

const TableWrapper = styled(Box)`
  border: 1px solid ${divider.middle};
  border-radius: 8px;
  overflow-x: auto;
`;

const StyledTable = styled("table")`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
  table-layout: fixed;

  thead tr {
    background-color: ${background.primary};
  }

  tbody tr:not(:last-child) {
    border-bottom: 1px solid ${divider.middle};
  }

  tfoot tr {
    border-top: 1px solid ${divider.high};
  }

  /* First column: fixed width + tighter horizontal padding for more label room */
  th:first-of-type,
  td:first-of-type {
    width: 120px;
    min-width: 120px;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const HeaderCell = styled("th")<{ $sticky?: boolean; $isTotal?: boolean }>`
  height: 40px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
  color: ${text.primary};
  white-space: nowrap;
  text-align: right;
  border-bottom: 1px solid ${divider.middle};

  ${({ $sticky }) =>
    $sticky &&
    `
    position: sticky;
    left: 0;
    z-index: 1;
    background-color: ${background.primary};
    text-align: left;
    min-width: 120px;
    border-right: 1px solid ${divider.middle};
  `}

  ${({ $isTotal }) =>
    $isTotal &&
    `
    border-left: 1px solid ${divider.high};
  `}
`;

const BodyCell = styled("td")<{ $sticky?: boolean; $isHeader?: boolean; $indent?: number; $isTotal?: boolean }>`
  height: 48px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: ${({ $isHeader }) => ($isHeader ? 700 : 400)};
  color: ${text.primary};
  white-space: nowrap;
  text-align: right;
  background-color: ${({ $isHeader }) => ($isHeader ? background.primary : background.white)};

  ${({ $sticky, $isHeader }) =>
    $sticky &&
    `
    position: sticky;
    left: 0;
    z-index: 1;
    text-align: left;
    overflow: hidden;
    background-color: ${$isHeader ? background.primary : background.white};
    border-right: 1px solid ${divider.middle};
  `}

  ${({ $isTotal }) =>
    $isTotal &&
    `
    border-left: 1px solid ${divider.high};
  `}

  ${({ $indent }) =>
    $indent &&
    `
    padding-left: ${12 + $indent * 12}px;
  `}
`;

const FooterCell = styled("td")<{ $sticky?: boolean; $isTotal?: boolean }>`
  height: 48px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${text.primary};
  white-space: nowrap;
  text-align: right;

  ${({ $sticky }) =>
    $sticky &&
    `
    position: sticky;
    left: 0;
    z-index: 1;
    text-align: left;
    font-weight: 700;
    background-color: ${background.white};
    border-right: 1px solid ${divider.middle};
  `}

  ${({ $isTotal }) =>
    $isTotal &&
    `
    border-left: 1px solid ${divider.high};
  `}
`;

// Fills the cell so clientWidth = available width for the truncation measurement.
const ClipText = styled("span")`
  display: block;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

const SelectWithChevron = styled(Box)`
  position: relative;
  min-width: 140px;
  .MuiSelect-select.MuiSelect-select {
    padding-right: 28px !important;
  }
  .MuiSelect-icon {
    display: none !important;
  }
`;

const ChevronIcon = styled(Box)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 3;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;
